import { useRef, useEffect, useState } from 'react';

/**
 * CameraFeed Component
 * 
 * Handles webcam access via WebRTC getUserMedia, frame extraction,
 * and transmission to parent component for backend processing.
 * 
 * Frame rate: 30 FPS
 * Resolution: 640x480 (optimal for face detection)
 * Format: JPEG for efficient transmission
 */
const CameraFeed = ({ active, onFrame, onFrameUpdate }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationFrameRef = useRef(null);

  const [error, setError] = useState(null);
  const [permissionGranted, setPermissionGranted] = useState(false);

  // Target frame rate
  const FPS = 30;
  const FRAME_INTERVAL = 1000 / FPS;

  /**
   * Initialize webcam access
   */
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 30 }
        },
        audio: false
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
        setPermissionGranted(true);
        setError(null);
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setError('Failed to access camera. Please grant camera permissions.');
      setPermissionGranted(false);
    }
  };

  /**
   * Stop webcam and release resources
   */
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setPermissionGranted(false);
  };

  /**
   * Capture and process video frames
   */
  const captureFrame = () => {
    if (!videoRef.current || !canvasRef.current || !active) {
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Check if video is ready
    if (video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationFrameRef.current = requestAnimationFrame(captureFrame);
      return;
    }

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to JPEG and send to backend
    canvas.toBlob(
      (blob) => {
        if (blob) {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64data = reader.result;
            onFrame(base64data);

            // Also provide frame for visualization
            onFrameUpdate({
              dataUrl: base64data,
              width: canvas.width,
              height: canvas.height,
              timestamp: Date.now()
            });
          };
          reader.readAsDataURL(blob);
        }
      },
      'image/jpeg',
      0.85  // Quality: balance between size and detail
    );

    // Schedule next frame capture
    setTimeout(() => {
      animationFrameRef.current = requestAnimationFrame(captureFrame);
    }, FRAME_INTERVAL);
  };

  /**
   * Start/stop camera based on active prop
   */
  useEffect(() => {
    if (active) {
      startCamera().then(() => {
        // Start frame capture after camera initializes
        setTimeout(() => {
          captureFrame();
        }, 500);
      });
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="camera-feed">
      <video
        ref={videoRef}
        className="video-element"
        autoPlay
        playsInline
        muted
      />

      {/* Hidden canvas for frame extraction */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />

      {/* Error display */}
      {error && (
        <div className="camera-error">
          <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p>{error}</p>
        </div>
      )}

      {/* Camera status indicator */}
      {active && permissionGranted && (
        <div className="camera-indicator">
          <div className="recording-dot" />
          <span>SCANNING</span>
        </div>
      )}
    </div>
  );
};

export default CameraFeed;
